import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Send, Minus } from 'lucide-react';
import { toast } from 'sonner';

const MASCOT_SRC = '/operio-mascot-3d.png';
const DISMISS_KEY = 'operio_chat_dismissed_at';
const DISMISS_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 h

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      const v = localStorage.getItem(DISMISS_KEY);
      if (!v) return false;
      return Date.now() - parseInt(v, 10) < DISMISS_WINDOW_MS;
    } catch (_) {
      return false;
    }
  });
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const dismiss = (e) => {
    e?.stopPropagation?.();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setDismissed(true);
  };

  const loadConversation = useCallback(async () => {
    try {
      const response = await api.getCurrentConversation();
      setConversation(response.data.conversation);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen && !conversation) {
      loadConversation();
    }
  }, [isOpen, conversation, loadConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage;
    setInputMessage('');
    setLoading(true);

    const tempUserMsg = {
      id: Date.now().toString(),
      sender_type: 'user',
      message: userMessage,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      const response = await api.sendChatMessage({
        conversation_id: conversation?.id,
        message: userMessage,
      });
      if (!conversation) {
        setConversation({ id: response.data.conversation_id });
      }
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== tempUserMsg.id);
        const newMessages = [response.data.user_message];
        if (response.data.ai_response) {
          newMessages.push({
            id: Date.now().toString() + '-ai',
            conversation_id: response.data.conversation_id,
            sender_type: 'ai',
            sender_name: 'Operio AI',
            message: response.data.ai_response,
            created_at: new Date().toISOString(),
          });
        }
        return [...filtered, ...newMessages];
      });
    } catch (error) {
      toast.error('Kunde inte skicka meddelande');
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMsg.id));
    } finally {
      setLoading(false);
    }
  };

  // Closed state — small clickable mascot with a tiny X to dismiss (Simon-feedback: mindre + kryssbar)
  if (dismissed) {
    // Diskret liten "återöppna"-knapp längst ner till höger så användaren kan få tillbaka bubblan
    return (
      <button
        onClick={() => {
          try { localStorage.removeItem(DISMISS_KEY); } catch (_) { /* ignore */ }
          setDismissed(false);
        }}
        className="fixed bottom-[84px] right-3 lg:bottom-4 sm:right-4 z-40 w-8 h-8 rounded-full bg-[#E8613A] text-white text-xs font-bold shadow-lg hover:scale-110 transition-transform flex items-center justify-center opacity-40 hover:opacity-100"
        data-testid="restore-chat-btn"
        title="Visa Operio-assistent igen"
      >
        ?
      </button>
    );
  }
  if (!isOpen) {
    return (
      <div className="fixed bottom-[84px] right-3 lg:bottom-5 sm:right-5 z-50 flex flex-col items-end gap-1" data-testid="chat-mascot-wrapper">
        <button
          type="button"
          onClick={dismiss}
          className="w-5 h-5 rounded-full bg-black/60 hover:bg-black/80 text-white text-[10px] flex items-center justify-center shadow-md opacity-70 hover:opacity-100 transition-opacity"
          data-testid="dismiss-chat-btn"
          aria-label="Dölj assistent"
          title="Dölj i 24 h"
        >
          <X size={11} />
        </button>
        <button
          onClick={() => setIsOpen(true)}
          className="group flex items-center focus:outline-none"
          data-testid="open-chat-btn"
          aria-label="Öppna Operio assistent"
        >
          <span className="relative inline-block">
            <span className="absolute inset-0 rounded-full bg-[#E8613A]/20 blur-xl scale-105 group-hover:scale-110 transition-transform duration-300" />
            <img
              src={MASCOT_SRC}
              alt="Operio maskot"
              className="relative w-12 h-12 sm:w-14 sm:h-14 drop-shadow-lg transition-transform duration-200 group-hover:scale-105 group-hover:-rotate-3"
            />
          </span>
        </button>
      </div>
    );
  }

  // Open state — mascot peeks above and "holds" the chat panel
  return (
    <div
      className="fixed bottom-[84px] right-3 lg:bottom-6 sm:right-6 z-50 flex flex-col items-end"
      data-testid="chat-widget"
    >
      {/* Mascot peeking above the chat — looks like he is holding it */}
      <img
        src={MASCOT_SRC}
        alt="Operio maskot"
        className={`relative z-10 drop-shadow-2xl pointer-events-none select-none transition-all duration-300 ${
          isMinimized ? 'w-16 sm:w-20 h-16 sm:h-20 -mb-3 sm:-mb-4 mr-2' : 'w-20 sm:w-32 h-20 sm:h-32 -mb-5 sm:-mb-7 mr-3'
        }`}
        data-testid="chat-mascot"
      />

      <div
        className={`relative bg-card rounded-2xl border border-border shadow-2xl overflow-hidden transition-all duration-200 ${
          isMinimized ? 'w-64 sm:w-72 h-12' : 'w-[calc(100vw-1.5rem)] sm:w-96 max-w-sm h-[80vh] sm:h-[520px] max-h-[600px]'
        }`}
      >
        {/* Header */}
        <div className="bg-[#1A1A1A] dark:bg-[#2A2520] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-sm font-medium">Operio AI</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              data-testid="minimize-chat-btn"
              aria-label="Minimera"
            >
              <Minus size={14} />
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                setIsMinimized(false);
              }}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              data-testid="close-chat-btn"
              aria-label="Stäng"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="h-[calc(100%-6.5rem)] overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-center py-10">
                  <img src={MASCOT_SRC} alt="Operio" className="w-16 h-16 mx-auto mb-3 opacity-90" />
                  <p className="text-sm text-foreground font-medium">Hej! Jag är Operio.</p>
                  <p className="text-xs text-muted-foreground mt-1">Fråga mig om projekt, fakturor, löner eller bokföring.</p>
                </div>
              )}

              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-2 ${msg.sender_type === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  {msg.sender_type !== 'user' && (
                    <img src={MASCOT_SRC} alt="" className="w-7 h-7 flex-shrink-0 mt-0.5" />
                  )}
                  <div
                    className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                      msg.sender_type === 'user'
                        ? 'bg-[#E8613A] text-white rounded-br-sm'
                        : msg.sender_type === 'support'
                        ? 'bg-green-100 dark:bg-green-900/30 text-foreground border border-green-200 dark:border-green-800 rounded-bl-sm'
                        : 'bg-muted text-foreground rounded-bl-sm'
                    }`}
                  >
                    <p className="whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex gap-2 items-end">
                  <img src={MASCOT_SRC} alt="" className="w-7 h-7 flex-shrink-0" />
                  <div className="bg-muted px-3 py-2 rounded-2xl rounded-bl-sm">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-card border-t border-border">
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Skriv ditt meddelande..."
                  disabled={loading}
                  data-testid="chat-input"
                  className="flex-1 h-9 text-sm rounded-full"
                />
                <Button
                  type="submit"
                  disabled={loading || !inputMessage.trim()}
                  className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-9 w-9 p-0 rounded-full"
                  data-testid="send-chat-btn"
                  aria-label="Skicka"
                >
                  <Send size={14} />
                </Button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatWidget;
